import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Calculator, Delete, Divide, X, Minus, Plus, Equal, Percent } from 'lucide-react';

/**
 * Decoy Calculator App
 * A fully functional calculator that hides GhostComm
 * Enter secret code to reveal the real app
 * 
 * Default exit code: 1337= (type 1337 then press =)
 */

const DecoyCalculator = ({ onExitDecoy, exitCode = '1337' }) => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [secretBuffer, setSecretBuffer] = useState('');

  // Check for secret exit code
  const checkExitCode = useCallback((newBuffer) => {
    if (newBuffer === exitCode) {
      onExitDecoy();
    }
  }, [exitCode, onExitDecoy]);

  const inputDigit = (digit) => {
    // Track secret code
    const newBuffer = secretBuffer + digit;
    setSecretBuffer(newBuffer.slice(-10)); // Keep last 10 chars
    
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
    setSecretBuffer('');
  };

  const clearEntry = () => {
    setDisplay('0');
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    setDisplay(String(value * -1));
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const performOperation = (nextOperation) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      let result;

      switch (operation) {
        case '+':
          result = currentValue + inputValue;
          break;
        case '-':
          result = currentValue - inputValue;
          break;
        case '*':
          result = currentValue * inputValue;
          break;
        case '/':
          result = inputValue !== 0 ? currentValue / inputValue : 'Error';
          break;
        default:
          result = inputValue;
      }

      setDisplay(String(result));
      setPreviousValue(result);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = () => {
    // Check exit code when = is pressed
    checkExitCode(secretBuffer);
    setSecretBuffer('');

    if (!operation || previousValue === null) {
      return;
    }

    const inputValue = parseFloat(display);
    let result;

    switch (operation) {
      case '+':
        result = previousValue + inputValue;
        break;
      case '-':
        result = previousValue - inputValue;
        break;
      case '*':
        result = previousValue * inputValue;
        break;
      case '/':
        result = inputValue !== 0 ? previousValue / inputValue : 'Error';
        break;
      default:
        result = inputValue;
    }

    setDisplay(String(result));
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(true);
  };

  const CalcButton = ({ children, onClick, variant = 'default', span = 1, className = '' }) => (
    <button
      onClick={onClick}
      className={`
        h-16 rounded-xl font-medium text-xl transition-all duration-150 active:scale-95
        ${variant === 'number' ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : ''}
        ${variant === 'operation' ? 'bg-blue-500 hover:bg-blue-400 text-white' : ''}
        ${variant === 'function' ? 'bg-zinc-500 hover:bg-zinc-400 text-white' : ''}
        ${variant === 'default' ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : ''}
        ${span === 2 ? 'col-span-2' : ''}
        ${className}
      `}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#1C1C1E' }}>
      <div className="w-full max-w-sm">
        {/* Calculator Header */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Calculator className="w-6 h-6 text-zinc-400" />
          <span className="text-zinc-400 text-lg font-medium">Calculator</span>
        </div>

        {/* Display */}
        <div className="bg-zinc-800 rounded-2xl p-6 mb-4">
          <div className="text-right">
            <div className="text-zinc-500 text-sm h-6">
              {previousValue !== null && operation && `${previousValue} ${operation}`}
            </div>
            <div 
              className="text-white font-light truncate"
              style={{ fontSize: display.length > 10 ? '2rem' : '3.5rem' }}
            >
              {display}
            </div>
          </div>
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-4 gap-3">
          <CalcButton variant="function" onClick={clear}>AC</CalcButton>
          <CalcButton variant="function" onClick={toggleSign}>+/-</CalcButton>
          <CalcButton variant="function" onClick={inputPercent}>
            <Percent className="w-5 h-5 mx-auto" />
          </CalcButton>
          <CalcButton variant="operation" onClick={() => performOperation('/')}>
            <Divide className="w-5 h-5 mx-auto" />
          </CalcButton>

          <CalcButton variant="number" onClick={() => inputDigit('7')}>7</CalcButton>
          <CalcButton variant="number" onClick={() => inputDigit('8')}>8</CalcButton>
          <CalcButton variant="number" onClick={() => inputDigit('9')}>9</CalcButton>
          <CalcButton variant="operation" onClick={() => performOperation('*')}>
            <X className="w-5 h-5 mx-auto" />
          </CalcButton>

          <CalcButton variant="number" onClick={() => inputDigit('4')}>4</CalcButton>
          <CalcButton variant="number" onClick={() => inputDigit('5')}>5</CalcButton>
          <CalcButton variant="number" onClick={() => inputDigit('6')}>6</CalcButton>
          <CalcButton variant="operation" onClick={() => performOperation('-')}>
            <Minus className="w-5 h-5 mx-auto" />
          </CalcButton>

          <CalcButton variant="number" onClick={() => inputDigit('1')}>1</CalcButton>
          <CalcButton variant="number" onClick={() => inputDigit('2')}>2</CalcButton>
          <CalcButton variant="number" onClick={() => inputDigit('3')}>3</CalcButton>
          <CalcButton variant="operation" onClick={() => performOperation('+')}>
            <Plus className="w-5 h-5 mx-auto" />
          </CalcButton>

          <CalcButton variant="number" span={2} onClick={() => inputDigit('0')}>0</CalcButton>
          <CalcButton variant="number" onClick={inputDecimal}>.</CalcButton>
          <CalcButton variant="operation" onClick={calculate}>
            <Equal className="w-5 h-5 mx-auto" />
          </CalcButton>
        </div>

        {/* Hidden hint - very subtle */}
        <div className="mt-8 text-center">
          <p className="text-zinc-700 text-xs">v2.1.4</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Decoy Mode Wrapper
 * Wraps the real app and shows decoy when activated
 */
export const DecoyModeWrapper = ({ children, isDecoyActive, onExitDecoy, exitCode }) => {
  if (isDecoyActive) {
    return <DecoyCalculator onExitDecoy={onExitDecoy} exitCode={exitCode} />;
  }
  return children;
};

/**
 * Hook for managing decoy mode
 */
export const useDecoyMode = (defaultExitCode = '1337') => {
  const [isDecoyActive, setIsDecoyActive] = useState(false);
  const [exitCode, setExitCode] = useState(defaultExitCode);

  const activateDecoy = useCallback(() => {
    setIsDecoyActive(true);
  }, []);

  const exitDecoy = useCallback(() => {
    setIsDecoyActive(false);
  }, []);

  // Listen for keyboard shortcut to activate decoy (Ctrl+Shift+D)
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        activateDecoy();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activateDecoy]);

  return {
    isDecoyActive,
    activateDecoy,
    exitDecoy,
    exitCode,
    setExitCode,
  };
};

export default DecoyCalculator;
